import { useCallback, useEffect, useState, useMemo } from 'react';

import { fetchJson } from '@api/ApiHelper';

import {
  PeoplePageResponse,
  PeoplePage,
} from '@components/people_tags/customPeopleTagTypes';

import { usePeopleManagementContext } from '../PeopleManagementContext';

// API response type from the identities endpoint
interface IdentityApiResponse {
  id: string;
  boundary_id: string;
  name: string;
  metadata?: string; // JSON string containing { hide: boolean }
  created_at: string;
  updated_at: string;
  face_count: number;
  identity_thumbnail_url: string;
}

const parseHiddenFromMetadata = (metadata?: string): boolean => {
  if (!metadata) {
    return false;
  }
  try {
    const parsed = JSON.parse(metadata) as { hide?: boolean };
    return !!parsed.hide;
  } catch {
    return false;
  }
};

// Mock data for local development/testing
const MOCK_IDENTITIES: IdentityApiResponse[] = [
  {
    id: 'identity-1',
    boundary_id: 'bf-123',
    name: 'John Smith',
    metadata: JSON.stringify({ hide: false }),
    created_at: '2024-01-15T10:30:00Z',
    updated_at: '2024-01-20T14:45:00Z',
    face_count: 12,
    identity_thumbnail_url: 'https://picsum.photos/seed/john/200/200',
  },
  {
    id: 'identity-2',
    boundary_id: 'bf-123',
    name: 'Jane Doe',
    metadata: JSON.stringify({ hide: false }),
    created_at: '2024-01-10T08:00:00Z',
    updated_at: '2024-01-18T09:30:00Z',
    face_count: 8,
    identity_thumbnail_url: 'https://picsum.photos/seed/jane/200/200',
  },
  {
    id: 'identity-3',
    boundary_id: 'bf-123',
    name: '',
    metadata: undefined,
    created_at: '2024-01-12T12:00:00Z',
    updated_at: '2024-02-01T12:00:00Z',
    face_count: 7,
    identity_thumbnail_url: 'https://picsum.photos/seed/unknown1/200/200',
  },
  {
    id: 'identity-4',
    boundary_id: 'bf-123',
    name: 'Alice Johnson',
    metadata: JSON.stringify({ hide: false }),
    created_at: '2024-01-05T16:20:00Z',
    updated_at: '2024-01-25T11:00:00Z',
    face_count: 15,
    identity_thumbnail_url: 'https://picsum.photos/seed/alice/200/200',
  },
  {
    id: 'identity-5',
    boundary_id: 'bf-123',
    name: 'Bob Williams',
    metadata: JSON.stringify({ hide: true }),
    created_at: '2024-01-08T09:15:00Z',
    updated_at: '2024-01-22T17:30:00Z',
    face_count: 6,
    identity_thumbnail_url: 'https://picsum.photos/seed/bob/200/200',
  },
  {
    id: 'identity-6',
    boundary_id: 'bf-123',
    name: '',
    metadata: undefined,
    created_at: '2024-02-05T14:00:00Z',
    updated_at: '2024-02-05T14:00:00Z',
    face_count: 1,
    identity_thumbnail_url: 'https://picsum.photos/seed/unknown2/200/200',
  },
  {
    id: 'identity-7',
    boundary_id: 'bf-123',
    name: 'Charlie Brown',
    metadata: JSON.stringify({ hide: false }),
    created_at: '2024-01-12T11:45:00Z',
    updated_at: '2024-01-28T16:00:00Z',
    face_count: 9,
    identity_thumbnail_url: 'https://picsum.photos/seed/charlie/200/200',
  },
  {
    id: 'identity-8',
    boundary_id: 'bf-123',
    name: 'Diana Prince',
    metadata: JSON.stringify({ hide: false }),
    created_at: '2024-01-03T07:30:00Z',
    updated_at: '2024-01-30T10:15:00Z',
    face_count: 20,
    identity_thumbnail_url: 'https://picsum.photos/seed/diana/200/200',
  },
  {
    id: 'identity-9',
    boundary_id: 'bf-123',
    name: '',
    metadata: undefined,
    created_at: '2024-02-08T18:00:00Z',
    updated_at: '2024-02-08T18:00:00Z',
    face_count: 2,
    identity_thumbnail_url: 'https://picsum.photos/seed/unknown3/200/200',
  },
  {
    id: 'identity-10',
    boundary_id: 'bf-123',
    name: 'Edward Norton',
    metadata: JSON.stringify({ hide: false }),
    created_at: '2024-01-20T13:00:00Z',
    updated_at: '2024-02-01T08:45:00Z',
    face_count: 5,
    identity_thumbnail_url: 'https://picsum.photos/seed/edward/200/200',
  },
  {
    id: 'identity-11',
    boundary_id: 'bf-123',
    name: 'Frank Miller',
    metadata: JSON.stringify({ hide: false }),
    created_at: '2024-01-18T14:20:00Z',
    updated_at: '2024-01-29T15:30:00Z',
    face_count: 11,
    identity_thumbnail_url: 'https://picsum.photos/seed/frank/200/200',
  },
  {
    id: 'identity-12',
    boundary_id: 'bf-123',
    name: 'Grace Lee',
    metadata: JSON.stringify({ hide: false }),
    created_at: '2024-01-22T09:00:00Z',
    updated_at: '2024-02-02T10:20:00Z',
    face_count: 18,
    identity_thumbnail_url: 'https://picsum.photos/seed/grace/200/200',
  },
  {
    id: 'identity-13',
    boundary_id: 'bf-123',
    name: '',
    metadata: undefined,
    created_at: '2024-01-25T11:15:00Z',
    updated_at: '2024-01-25T11:15:00Z',
    face_count: 4,
    identity_thumbnail_url: 'https://picsum.photos/seed/unknown4/200/200',
  },
  {
    id: 'identity-14',
    boundary_id: 'bf-123',
    name: 'Henry Davis',
    metadata: JSON.stringify({ hide: false }),
    created_at: '2024-01-14T16:45:00Z',
    updated_at: '2024-01-26T12:00:00Z',
    face_count: 13,
    identity_thumbnail_url: 'https://picsum.photos/seed/henry/200/200',
  },
  {
    id: 'identity-15',
    boundary_id: 'bf-123',
    name: 'Isabella Martinez',
    metadata: JSON.stringify({ hide: false }),
    created_at: '2024-01-07T08:30:00Z',
    updated_at: '2024-01-24T14:15:00Z',
    face_count: 22,
    identity_thumbnail_url: 'https://picsum.photos/seed/isabella/200/200',
  },
  {
    id: 'identity-16',
    boundary_id: 'bf-123',
    name: 'Jack Wilson',
    metadata: JSON.stringify({ hide: true }),
    created_at: '2024-01-11T10:00:00Z',
    updated_at: '2024-01-27T09:45:00Z',
    face_count: 3,
    identity_thumbnail_url: 'https://picsum.photos/seed/jack/200/200',
  },
  {
    id: 'identity-17',
    boundary_id: 'bf-123',
    name: '',
    metadata: undefined,
    created_at: '2024-02-03T13:30:00Z',
    updated_at: '2024-02-03T13:30:00Z',
    face_count: 6,
    identity_thumbnail_url: 'https://picsum.photos/seed/unknown5/200/200',
  },
  {
    id: 'identity-18',
    boundary_id: 'bf-123',
    name: 'Karen Anderson',
    metadata: JSON.stringify({ hide: false }),
    created_at: '2024-01-16T12:20:00Z',
    updated_at: '2024-01-31T11:10:00Z',
    face_count: 16,
    identity_thumbnail_url: 'https://picsum.photos/seed/karen/200/200',
  },
  {
    id: 'identity-19',
    boundary_id: 'bf-123',
    name: 'Liam Thompson',
    metadata: JSON.stringify({ hide: false }),
    created_at: '2024-01-09T15:00:00Z',
    updated_at: '2024-01-23T16:30:00Z',
    face_count: 10,
    identity_thumbnail_url: 'https://picsum.photos/seed/liam/200/200',
  },
  {
    id: 'identity-20',
    boundary_id: 'bf-123',
    name: 'Maria Garcia',
    metadata: JSON.stringify({ hide: false }),
    created_at: '2024-01-13T07:45:00Z',
    updated_at: '2024-01-21T13:20:00Z',
    face_count: 19,
    identity_thumbnail_url: 'https://picsum.photos/seed/maria/200/200',
  },
  {
    id: 'identity-21',
    boundary_id: 'bf-123',
    name: '',
    metadata: undefined,
    created_at: '2024-02-06T10:00:00Z',
    updated_at: '2024-02-06T10:00:00Z',
    face_count: 8,
    identity_thumbnail_url: 'https://picsum.photos/seed/unknown6/200/200',
  },
  {
    id: 'identity-22',
    boundary_id: 'bf-123',
    name: 'Noah Brown',
    metadata: JSON.stringify({ hide: false }),
    created_at: '2024-01-19T11:30:00Z',
    updated_at: '2024-02-04T09:15:00Z',
    face_count: 14,
    identity_thumbnail_url: 'https://picsum.photos/seed/noah/200/200',
  },
  {
    id: 'identity-23',
    boundary_id: 'bf-123',
    name: 'Olivia Taylor',
    metadata: JSON.stringify({ hide: false }),
    created_at: '2024-01-06T14:10:00Z',
    updated_at: '2024-01-17T15:45:00Z',
    face_count: 17,
    identity_thumbnail_url: 'https://picsum.photos/seed/olivia/200/200',
  },
  {
    id: 'identity-24',
    boundary_id: 'bf-123',
    name: 'Peter Jackson',
    metadata: JSON.stringify({ hide: true }),
    created_at: '2024-01-04T09:20:00Z',
    updated_at: '2024-01-19T10:30:00Z',
    face_count: 4,
    identity_thumbnail_url: 'https://picsum.photos/seed/peter/200/200',
  },
  {
    id: 'identity-25',
    boundary_id: 'bf-123',
    name: 'Quinn White',
    metadata: JSON.stringify({ hide: false }),
    created_at: '2024-01-17T13:00:00Z',
    updated_at: '2024-01-30T14:00:00Z',
    face_count: 21,
    identity_thumbnail_url: 'https://picsum.photos/seed/quinn/200/200',
  },
  {
    id: 'identity-26',
    boundary_id: 'bf-123',
    name: '',
    metadata: undefined,
    created_at: '2024-02-07T15:45:00Z',
    updated_at: '2024-02-07T15:45:00Z',
    face_count: 5,
    identity_thumbnail_url: 'https://picsum.photos/seed/unknown7/200/200',
  },
  {
    id: 'identity-27',
    boundary_id: 'bf-123',
    name: 'Rachel Green',
    metadata: JSON.stringify({ hide: false }),
    created_at: '2024-01-21T08:15:00Z',
    updated_at: '2024-02-03T12:30:00Z',
    face_count: 25,
    identity_thumbnail_url: 'https://picsum.photos/seed/rachel/200/200',
  },
  {
    id: 'identity-28',
    boundary_id: 'bf-123',
    name: 'Samuel Harris',
    metadata: JSON.stringify({ hide: false }),
    created_at: '2024-01-02T10:30:00Z',
    updated_at: '2024-01-16T11:45:00Z',
    face_count: 7,
    identity_thumbnail_url: 'https://picsum.photos/seed/samuel/200/200',
  },
  {
    id: 'identity-29',
    boundary_id: 'bf-123',
    name: 'Tina Chen',
    metadata: JSON.stringify({ hide: false }),
    created_at: '2024-01-24T12:00:00Z',
    updated_at: '2024-02-05T13:15:00Z',
    face_count: 23,
    identity_thumbnail_url: 'https://picsum.photos/seed/tina/200/200',
  },
  {
    id: 'identity-30',
    boundary_id: 'bf-123',
    name: '',
    metadata: undefined,
    created_at: '2024-02-09T09:30:00Z',
    updated_at: '2024-02-09T09:30:00Z',
    face_count: 3,
    identity_thumbnail_url: 'https://picsum.photos/seed/unknown8/200/200',
  },
];

// Toggle this to use mock data instead of real API
const USE_MOCK_DATA = false;

const transformFaceDataToResponse = (faces: IdentityApiResponse[]): PeoplePageResponse => {
  const transformedData: PeoplePage[] = faces.map((face) => ({
    thumbnail_url: face.identity_thumbnail_url || '',
    person_id: face.id,
    name: face.name || '',
    isHidden: parseHiddenFromMetadata(face.metadata),
    created_at: face.created_at,
    face_count: face.face_count,
  }));

  return {
    data: transformedData,
    meta: {
      bf_processed: true,
      current_page: 1,
      next_page: null,
      prev_page: null,
      total_count: transformedData.length,
      total_pages: 1,
    },
  };
};

export interface UseFetchPeopleHubDataReturn {
  initialLoadingState: boolean;
  peopleData: PeoplePage[];
  allPeopleData: PeoplePage[];
  peopleDataError: boolean;
  peopleDataLoading: boolean;
  refetchPeopleData: () => Promise<void>;
  allCount: number;
  namedCount: number;
  unnamedCount: number;
  bfProcessed: boolean;
  totalPages: number;
  currentPage: number;
}

const ITEMS_PER_PAGE = 24;

// Helper functions for name extraction
const getFirstName = (name: string): string => {
  const trimmed = name?.trim() || '';
  if (!trimmed) return '';
  const parts = trimmed.split(/\s+/);
  return parts[0] || '';
};

const getLastName = (name: string): string => {
  const trimmed = name?.trim() || '';
  if (!trimmed) return '';
  const parts = trimmed.split(/\s+/);
  // For single names, use the name itself as both first and last
  return parts.length > 1 ? parts.at(-1) : parts[0];
};

// Sort comparators
const sortComparators = {
  upload_date_newest: (a: PeoplePage, b: PeoplePage): number => {
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return dateB - dateA;
  },
  upload_date_oldest: (a: PeoplePage, b: PeoplePage): number => {
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return dateA - dateB;
  },
  frequency_most: (a: PeoplePage, b: PeoplePage): number => {
    return (b.face_count ?? 0) - (a.face_count ?? 0);
  },
  frequency_least: (a: PeoplePage, b: PeoplePage): number => {
    return (a.face_count ?? 0) - (b.face_count ?? 0);
  },
  first_name_asc: (a: PeoplePage, b: PeoplePage): number => {
    return getFirstName(a.name).localeCompare(getFirstName(b.name));
  },
  first_name_desc: (a: PeoplePage, b: PeoplePage): number => {
    return getFirstName(b.name).localeCompare(getFirstName(a.name));
  },
  last_name_asc: (a: PeoplePage, b: PeoplePage): number => {
    return getLastName(a.name).localeCompare(getLastName(b.name));
  },
  last_name_desc: (a: PeoplePage, b: PeoplePage): number => {
    return getLastName(b.name).localeCompare(getLastName(a.name));
  },
};

export const useFetchPeopleHubData = (): UseFetchPeopleHubDataReturn => {
  const { state } = usePeopleManagementContext();
  const [rawPeopleData, setRawPeopleData] = useState<PeoplePageResponse>(undefined);
  const [peopleDataLoading, setPeopleDataLoading] = useState<boolean>(true);
  const [initialLoadingState, setInitialLoadingState] = useState<boolean>(true);
  const [peopleDataError, setPeopleDataError] = useState<boolean>(false);

  const loadPeopleData = useCallback(async (): Promise<void> => {
    try {
      setPeopleDataLoading(true);
      setRawPeopleData(undefined);

      let response: IdentityApiResponse[];

      if (USE_MOCK_DATA) {
        // Simulate network delay for realistic testing
        await new Promise((resolve) => setTimeout(resolve, 500));
        response = MOCK_IDENTITIES;
      } else {
        // Get brandfolder key from global BFG object
        const brandfolderKey = BFG.brandfolder_key;

        if (!brandfolderKey) {
          throw new Error('Brandfolder key not found');
        }

        // Make API call to fetch identities
        response = await fetchJson<IdentityApiResponse[]>({
          url: `/api/v4/private/people_tagging/brandfolders/${brandfolderKey}/identities`,
          method: 'GET',
        });
      }

      // Transform API response to expected format
      const transformedData = transformFaceDataToResponse(response);
      setRawPeopleData(transformedData);
      setPeopleDataError(false);
    } catch (error) {
      console.error('Error fetching people data:', error);
      setRawPeopleData(undefined);
      setPeopleDataError(true);
    } finally {
      setPeopleDataLoading(false);
      setInitialLoadingState(false);
    }
  }, []);

  useEffect(() => {
    loadPeopleData();
  }, [loadPeopleData]);

  // Calculate counts and apply filtering/sorting/pagination based on state
  const { filteredData, allCount, namedCount, unnamedCount, totalPages } = useMemo(() => {
    if (!rawPeopleData?.data) {
      return { filteredData: [], allCount: 0, namedCount: 0, unnamedCount: 0, totalPages: 0 };
    }

    const allData = rawPeopleData.data;
    const named = allData.filter((person) => person.name && person.name.trim() !== '');
    const unnamed = allData.filter((person) => !person.name || person.name.trim() === '');

    let filtered = [...allData];

    // Apply search filter
    if (state.search.trim()) {
      const searchTerm = state.search.toLowerCase();
      filtered = filtered.filter((person) => {
        const name = person.name || 'unnamed';
        return name.toLowerCase().includes(searchTerm);
      });
    }

    // Apply category filter
    switch (state.filter) {
      case 'named':
        filtered = filtered.filter((person) => person.name && person.name.trim() !== '');
        break;
      case 'unnamed':
        filtered = filtered.filter((person) => !person.name || person.name.trim() === '');
        break;
      case 'all':
      default:
        // No additional filtering needed
        break;
    }

    filtered.sort((a, b) => {
      const aNamed = !!(a.name && a.name.trim() !== '');
      const bNamed = !!(b.name && b.name.trim() !== '');

      // Pin named identities to the top when using default sort or name-based sorts
      const nameBasedSorts = ['first_name_asc', 'first_name_desc', 'last_name_asc', 'last_name_desc'];
      const isNameBasedSort = nameBasedSorts.includes(state.sort);

      if (state.sort === 'default' || isNameBasedSort) {
        if (aNamed && !bNamed) return -1;
        if (!aNamed && bNamed) return 1;
      }

      const comparator = sortComparators[state.sort as keyof typeof sortComparators];
      if (comparator) {
        return comparator(a, b);
      }
      return 0;
    });

    // Calculate total pages
    const calculatedTotalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

    // Apply pagination
    const startIndex = (state.page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedData = filtered.slice(startIndex, endIndex);

    return {
      filteredData: paginatedData,
      allCount: allData.length,
      namedCount: named.length,
      unnamedCount: unnamed.length,
      totalPages: calculatedTotalPages
    };
  }, [rawPeopleData?.data, state.search, state.filter, state.sort, state.page]);

  return {
    initialLoadingState,
    peopleData: filteredData,
    allPeopleData: rawPeopleData?.data || [],
    peopleDataError,
    peopleDataLoading,
    refetchPeopleData: loadPeopleData,
    allCount,
    namedCount,
    unnamedCount,
    bfProcessed: rawPeopleData?.meta.bf_processed ?? false,
    totalPages,
    currentPage: state.page,
  };
};
